import React, { useEffect } from "react";
import { useNavigate } from 'react-router-dom';
import { motion } from "framer-motion";
import { useTheme } from "../../../../context/ThemeContext";
import { Brain, Zap, Clock, Shield, ChevronRight } from "lucide-react";

const Aboutus = () => {
  const navigate = useNavigate();
  const { isDarkTheme } = useTheme();
  
  useEffect(() => {
    const aboutSection = document.querySelector(".about");
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("visible");
          }
        });
      },
      { threshold: 0.2 }
    );

    observer.observe(aboutSection);
    return () => observer.unobserve(aboutSection);
  }, []);

  const navigateToauth = () => {
    navigate('/auth');
  };

  const features = [
    {
      icon: <Brain className="w-8 h-8" />,
      title: "AI-Powered Intelligence",
      description: "Advanced GPT technology trained specifically for petroleum engineering challenges"
    },
    {
      icon: <Zap className="w-8 h-8" />,
      title: "Lightning Fast Responses",
      description: "Get instant answers to complex technical queries in real-time"
    },
    {
      icon: <Clock className="w-8 h-8" />,
      title: "24/7 Availability",
      description: "Access expert-level support anytime, anywhere across all time zones"
    },
    {
      icon: <Shield className="w-8 h-8" />,
      title: "Enterprise Security",
      description: "Bank-grade security protocols to protect your sensitive engineering data"
    }
  ];

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };

  const floatVariants = {
    animate: {
      y: [-10, 10, -10],
      transition: {
        duration: 3,
        repeat: Infinity,
        ease: "easeInOut"
      }
    }
  };

  return (
    <motion.section
      id="about"
      className={`about py-24 px-4 ${
        isDarkTheme 
          ? 'bg-gradient-to-r from-black/90 to-black/80' 
          : 'bg-gradient-to-l from-amber-300/60 to-amber-500'
      }`}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 1 }}
    >
      {/* Floating Elements with Animation */}
      <motion.div 
        className={`absolute right-0 top-0 w-32 h-32 ${
          isDarkTheme 
            ? 'bg-amber-900/10' 
            : 'bg-amber-700/10'
        } blur-3xl`}
        variants={floatVariants}
        animate="animate"
      />
      <motion.div 
        className={`absolute left-0 bottom-0 w-40 h-40 ${
          isDarkTheme 
            ? 'bg-amber-900/10' 
            : 'bg-amber-700/10'
        } blur-3xl`}
        variants={floatVariants}
        animate="animate"
        transition={{ delay: 1.5 }}
      />

      <div className="max-w-7xl mx-auto relative">
        {/* Header Section */}
        <motion.div 
          className="text-center mb-16"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          <motion.h2
            className={`text-3xl lg:text-4xl font-bold mb-6 ${
              isDarkTheme 
                ? 'text-amber-400' 
                : 'text-white'
            }`}
            variants={itemVariants}
          >
            About OGFLEET
          </motion.h2>
          
          <motion.p
            className={`max-w-3xl mx-auto text-lg ${
              isDarkTheme 
                ? 'text-gray-300' 
                : 'text-white/90'
            }`}
            variants={itemVariants}
          >
            Revolutionizing petroleum engineering with cutting-edge AI technology. 
            OGFLEET combines the power of GPT with deep domain expertise to deliver 
            intelligent solutions that transform how engineers work.
          </motion.p>
        </motion.div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-12">
          {/* Left: Main Description */}
          <motion.div
            className="space-y-6"
            initial={{ x: -50, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.3 }}
          >
            <motion.div 
              className={`p-8 rounded-2xl shadow-xl border ${
                isDarkTheme 
                  ? 'bg-gray-900/50 border-gray-800 text-white' 
                  : 'bg-white/90 border-gray-100 text-black'
              }`}
              whileHover={{ 
                scale: 1.02,
                transition: { duration: 0.3 }
              }}
            >
              <h3 className={`text-2xl font-bold mb-4 ${
                isDarkTheme 
                  ? 'text-amber-400' 
                  : 'text-amber-600'
              }`}>
                The Future of Petroleum Engineering
              </h3>
              <p className={`text-lg leading-relaxed ${
                isDarkTheme 
                  ? 'text-gray-300' 
                  : 'text-gray-700'
              }`}>
                Powered by advanced GPT technology, OGFLEET is your intelligent assistant 
                for all petroleum engineering challenges. From drilling optimization to 
                reservoir management, get expert-level insights and solutions instantly.
              </p>
            </motion.div>

            <motion.div 
              className={`p-6 rounded-xl ${
                isDarkTheme 
                  ? 'bg-amber-400/10 border border-amber-400/20' 
                  : 'bg-white/80 border border-amber-200'
              }`}
              whileHover={{ 
                scale: 1.05,
                transition: { duration: 0.3 }
              }}
            >
              <p className={`text-center font-semibold ${
                isDarkTheme 
                  ? 'text-amber-300' 
                  : 'text-amber-700'
              }`}>
                "Transform your engineering workflow with AI that understands your domain"
              </p>
            </motion.div>
          </motion.div>

          {/* Right: Feature Cards */}
          <motion.div
            className="grid grid-cols-1 sm:grid-cols-2 gap-6"
            initial={{ x: 50, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            {features.map((feature, index) => (
              <motion.div
                key={index}
                className={`p-6 rounded-xl shadow-lg border transform transition-all duration-300 hover:scale-105 hover:shadow-xl ${
                  isDarkTheme
                    ? 'bg-gray-900/50 border-gray-800 text-white hover:bg-gray-800/50'
                    : 'bg-white/90 border-gray-100 text-black hover:bg-white'
                }`}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.5 + index * 0.1 }}
                whileHover={{ 
                  y: -5,
                  transition: { duration: 0.2 }
                }}
              >
                <motion.div 
                  className={`p-3 rounded-lg mb-4 inline-block ${
                    isDarkTheme 
                      ? 'bg-amber-400/20 text-amber-400' 
                      : 'bg-amber-100 text-amber-600'
                  }`}
                  whileHover={{ 
                    rotate: 360,
                    transition: { duration: 0.5 }
                  }}
                >
                  {feature.icon}
                </motion.div>
                <h4 className={`text-lg font-bold mb-2 ${
                  isDarkTheme 
                    ? 'text-amber-400' 
                    : 'text-amber-600'
                }`}>
                  {feature.title}
                </h4>
                <p className={`text-sm ${
                  isDarkTheme 
                    ? 'text-gray-300' 
                    : 'text-gray-600'
                }`}>
                  {feature.description}
                </p>
              </motion.div>
            ))}
          </motion.div>
        </div>

        {/* Shorter Call to Action */}
        <motion.div
          className="text-center"
          initial={{ y: 30, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.6 }}
        >
          <motion.div 
            className={`relative overflow-hidden rounded-3xl shadow-2xl border ${
              isDarkTheme 
                ? 'bg-gradient-to-r from-gray-900/80 to-gray-800/80 border-amber-400/20 text-white' 
                : 'bg-gradient-to-r from-white/95 to-amber-50/95 border-amber-200 text-black'
            }`}
            whileHover={{ 
              scale: 1.02,
              transition: { duration: 0.3 }
            }}
          >
            {/* Background Pattern */}
            <div className={`absolute inset-0 opacity-5 ${
              isDarkTheme ? 'bg-amber-400' : 'bg-amber-600'
            }`} 
            style={{
              backgroundImage: `radial-gradient(circle at 25% 25%, currentColor 1px, transparent 1px), radial-gradient(circle at 75% 75%, currentColor 1px, transparent 1px)`,
              backgroundSize: '20px 20px'
            }} />
            
            <div className="relative p-8">
              <motion.div
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold mb-4 ${
                  isDarkTheme 
                    ? 'bg-amber-400/20 text-amber-300 border border-amber-400/30' 
                    : 'bg-amber-100 text-amber-700 border border-amber-200'
                }`}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.8 }}
              >
                <div className={`w-2 h-2 rounded-full ${
                  isDarkTheme ? 'bg-amber-400' : 'bg-amber-600'
                } animate-pulse`} />
                Join Thousands of Engineers
              </motion.div>
              
              <h3 className={`text-2xl lg:text-3xl font-bold mb-4 ${
                isDarkTheme 
                  ? 'text-amber-400' 
                  : 'text-amber-600'
              }`}>
                Ready to Transform Your Operations?
              </h3>
              
              <p className={`text-base mb-6 max-w-md mx-auto ${
                isDarkTheme 
                  ? 'text-gray-300' 
                  : 'text-gray-600'
              }`}>
                Experience the future of petroleum engineering with AI-powered solutions
              </p>
              
              <motion.button
                onClick={navigateToauth}
                className={`group relative overflow-hidden px-8 py-4 rounded-full font-bold text-lg shadow-lg transition-all duration-300 flex items-center gap-3 mx-auto ${
                  isDarkTheme
                    ? 'bg-gradient-to-r from-amber-400 to-amber-600 hover:from-amber-500 hover:to-amber-700 text-white'
                    : 'bg-gradient-to-r from-amber-500 to-amber-700 hover:from-amber-600 hover:to-amber-800 text-white'
                }`}
                whileHover={{ 
                  scale: 1.05,
                  transition: { duration: 0.2 }
                }}
                whileTap={{ 
                  scale: 0.95,
                  transition: { duration: 0.1 }
                }}
              >
                <span>Get Started Today</span>
                <motion.div
                  animate={{ x: [0, 4, 0] }}
                  transition={{ duration: 1.5, repeat: Infinity }}
                  className="flex items-center"
                >
                  <ChevronRight className="w-5 h-5" />
                </motion.div>
                
                {/* Button shine effect */}
                <motion.div
                  className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"
                  initial={{ x: '-100%' }}
                  whileHover={{ x: '100%' }}
                  transition={{ duration: 0.6 }}
                />
              </motion.button>
              
              <motion.p 
                className={`text-xs mt-4 ${
                  isDarkTheme ? 'text-gray-400' : 'text-gray-500'
                }`}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1 }}
              >
                Free to start • No credit card required
              </motion.p>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </motion.section>
  );
};

export default Aboutus;
